import { useEffect, useMemo, useState } from 'react'
import { Coins, Plus, Trophy, TrendingUp, Users, X } from 'lucide-react'

import {
  TIER_BRAND_LABEL,
  TIER_MEMBER_CAP,
  TIER_MONTHLY_PRICE,
} from '@/lib/squadron/tierMeta'
import type { SubscriptionTier } from '@/lib/api/schemas'

/**
 * Interactive ROI calculator for the pricing page.
 *
 * Inputs:
 *   - Squadron size + monthly fee per member
 *   - Tournaments per month + avg entry fee + avg prize pool
 *   - Free-form outgoings list (Discord Nitro, Twitch sub, etc.)
 *
 * Output: gross income, all costs itemised, net to squadron.
 * Recommends the cheapest tier that supports the configuration
 * (member count + whether paid tournaments are in play).
 *
 * Math notes:
 *   - Stripe UK fee: 1.4% + 20p per successful transaction. Modelled
 *     for both member subs (one per member per month) and tournament
 *     entries (one per player per tournament).
 *   - Entries = prize pool (balanced model — owner doesn't keep
 *     entry money, doesn't top up). Stripe fees on entries are still
 *     borne by the squadron unless they pass them to entrants.
 *   - e404 service fee = 0.4% of member subscriptions + 1% of paid
 *     prize pools, shown as one combined line on top of the flat
 *     squadron sub. (Pre-2026-07 this calculator took nothing on member
 *     subs; the fee rebuild moved e404 to 0.4% of dues.)
 *   - Tournament cash flow itself is net-zero for the owner in this
 *     model (money in = money out), so it doesn't show as income —
 *     only its costs (service fee + Stripe) bite into the net.
 */
// The only monthly member fees an owner can actually set: £0 (free squadron)
// or a rung of the prescribed ladder £2.99–£9.99. Mirror of
// MEMBERSHIP_FEE_LADDER_PENCE in e404_infra/functions/squadrons/index.js — keep
// in sync. The slider indexes into this so it can never land on a value the app
// would reject.
const MEMBER_FEE_OPTIONS = [0, 2.99, 3.99, 4.99, 5.99, 6.99, 7.99, 8.99, 9.99]

// Paid-tournament entry fee ceiling — matches the create-event input (max £100,
// 50p steps) and the backend cap (caps.entryFeePenceMax = £100).
const MAX_ENTRY_FEE = 100
const ENTRY_FEE_STEP = 0.5

// Prize-pot ceiling — matches the backend cap `caps.prizePoolPenceMax`
// (250000 pence = £2,500). Was £500 here, which understated what an owner
// can actually put up by five times.
const MAX_PRIZE_POOL = 2500

const TIER_ORDER: SubscriptionTier[] = ['FREE', 'LEVEL_1', 'LEVEL_2', 'LEVEL_3']

/**
 * What each tier can actually do, mirroring `EVENT_TIER_LIMITS` in
 * `e404_infra/functions/events/index.js` and `contracts/TIER_CAPABILITIES.md`.
 *
 * The calculator is tier-FIRST: you choose a plan, and it only lets you
 * configure what that plan permits. A control you cannot use is disabled and
 * says which tier unlocks it, rather than letting you build a configuration
 * the app would reject and correcting you afterwards.
 */
const TIER_CAPS: Record<
  SubscriptionTier,
  { members: number; tournaments: boolean; paidTournaments: boolean }
> = {
  // FREE has no TOURNAMENT in allowedTypes — not even free entry.
  FREE:    { members: TIER_MEMBER_CAP.FREE,    tournaments: false, paidTournaments: false },
  LEVEL_1: { members: TIER_MEMBER_CAP.LEVEL_1, tournaments: true,  paidTournaments: false },
  LEVEL_2: { members: TIER_MEMBER_CAP.LEVEL_2, tournaments: true,  paidTournaments: false },
  // allowPaid is Gold-only.
  LEVEL_3: { members: TIER_MEMBER_CAP.LEVEL_3, tournaments: true,  paidTournaments: true },
}

/** The cheapest tier that unlocks a capability — drives the upgrade prompts. */
function firstTierWith(pred: (c: (typeof TIER_CAPS)[SubscriptionTier]) => boolean) {
  return TIER_ORDER.find((t) => pred(TIER_CAPS[t])) ?? 'LEVEL_3'
}
const FIRST_TOURNAMENT_TIER = firstTierWith((c) => c.tournaments)
const FIRST_PAID_TIER = firstTierWith((c) => c.paidTournaments)

/** The next tier up, or null at the top. */
/**
 * Tier name for running prose.
 *
 * `TIER_BRAND_LABEL` is uppercase because it is the badge styling used across
 * the app, and that is right on a badge. Dropped into a sentence it reads as
 * shouting — "FREE caps out at 10 members. Upgrade to BRONZE" — so prose gets
 * a title-cased version instead.
 */
function tierName(tier: SubscriptionTier): string {
  const raw = TIER_BRAND_LABEL[tier]
  return raw.charAt(0) + raw.slice(1).toLowerCase()
}

function nextTier(tier: SubscriptionTier): SubscriptionTier | null {
  const i = TIER_ORDER.indexOf(tier)
  return i >= 0 && i < TIER_ORDER.length - 1 ? TIER_ORDER[i + 1] : null
}

export function PricingCalculator() {
  // Tier first. Everything below is constrained by this choice, so the
  // calculator can only ever describe a squadron the app would actually allow.
  const [tier, setTier] = useState<SubscriptionTier>('LEVEL_1')
  const caps = TIER_CAPS[tier]

  const [members, setMembers] = useState(25)
  // £4.99 default (index 3) — the nearest rung to the old £5 default.
  const [feeIdx, setFeeIdx] = useState(3)
  const feePerMember = MEMBER_FEE_OPTIONS[feeIdx]
  const [tournamentsPerMonth, setTournamentsPerMonth] = useState(2)
  const [avgEntryFee, setAvgEntryFee] = useState(5)
  const [avgPrizePool, setAvgPrizePool] = useState(80)
  const [outgoings, setOutgoings] = useState<OutgoingRow[]>([
    { id: 'default-1', name: 'Discord Nitro', cost: 10 },
    { id: 'default-2', name: 'Twitch sub bundle', cost: 5 },
  ])

  // Moving DOWN a tier has to pull the inputs back with it, or the numbers
  // would keep describing a squadron this plan cannot run.
  useEffect(() => {
    setMembers((m) => Math.min(m, caps.members))
    if (!caps.tournaments) setTournamentsPerMonth(0)
    if (!caps.paidTournaments) {
      setAvgEntryFee(0)
      setAvgPrizePool(0)
    }
  }, [caps.members, caps.tournaments, caps.paidTournaments])

  const result = useMemo(() => {
    // Member fees
    const memberGross = members * feePerMember
    const memberStripeFee = members * (feePerMember * 0.014 + 0.2)

    // Tournament fees — derive players from pool / entry (rounded
    // down; floor avoids overstating Stripe fees on weird inputs).
    const playersPerTournament =
      avgEntryFee > 0 ? Math.floor(avgPrizePool / avgEntryFee) : 0
    const tournamentPlatformFee = tournamentsPerMonth * avgPrizePool * 0.01
    const tournamentStripeFee =
      tournamentsPerMonth *
      playersPerTournament *
      (avgEntryFee * 0.014 + 0.2)
    const runsPaidTournaments =
      tournamentsPerMonth > 0 && avgEntryFee > 0 && avgPrizePool > 0

    // e404 service fee — the platform's cut on money that flows through:
    // 0.4% of member subscriptions + 1% of paid prize pools. Shown as one
    // line so owners see the whole "what e404 takes" number at a glance.
    // (Simplified: 0.4% is charged on the tax-exclusive fee in production;
    // this sketch doesn't model VAT, so it applies it to the gross.)
    const memberPlatformFee = memberGross * 0.004
    const e404ServiceFee = memberPlatformFee + tournamentPlatformFee

    // Tier recommendation considers BOTH headcount AND whether the
    // owner runs paid tournaments — paid pools need BRONZE+, so a
    // 5-member squadron running paid bracket nights still recommends
    // BRONZE.
    const runsAnyTournaments = tournamentsPerMonth > 0
    // The visitor picked the tier, so we cost what they picked rather than
    // second-guessing them. The inputs are already clamped to it.
    const tierCost = TIER_MONTHLY_PRICE[tier]

    // Custom outgoings — sum of user-added recurring costs.
    const customOutgoings = outgoings.reduce(
      (sum, o) => sum + (Number.isFinite(o.cost) ? o.cost : 0),
      0,
    )

    const totalCosts =
      memberStripeFee +
      tierCost +
      e404ServiceFee +
      tournamentStripeFee +
      customOutgoings
    const net = memberGross - totalCosts
    const roiMultiple = tierCost > 0 ? Math.floor(net / tierCost) : null

    return {
      memberGross,
      memberStripeFee,
      memberPlatformFee,
      tournamentPlatformFee,
      e404ServiceFee,
      tournamentStripeFee,
      playersPerTournament,
      runsPaidTournaments,
      tierCost,
      customOutgoings,
      totalCosts,
      net,
      roiMultiple,
      // A tournament's field is capped at the tier's member cap (25/50/300),
      // enforced on create and update. The player count here is derived from
      // pool / entry, so it can imply a field the app would reject — say so
      // rather than quietly costing Stripe fees on players who can't enter.
      playersOverCap:
        runsAnyTournaments && playersPerTournament > caps.members,
      tierParticipantCap: caps.members,
    }
  }, [
    tier,
    caps.members,
    members,
    feePerMember,
    tournamentsPerMonth,
    avgEntryFee,
    avgPrizePool,
    outgoings,
  ])

  // Cap the outgoings list so the calculator stays a sketch, not an
  // accounting tool. Keeps the output column scannable too.
  const MAX_OUTGOINGS = 10
  const atOutgoingLimit = outgoings.length >= MAX_OUTGOINGS

  function addOutgoing() {
    if (atOutgoingLimit) return
    setOutgoings((prev) => [
      ...prev,
      { id: `o-${nextId()}`, name: '', cost: 0 },
    ])
  }
  function updateOutgoing(id: string, patch: Partial<OutgoingRow>) {
    setOutgoings((prev) =>
      prev.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    )
  }
  function removeOutgoing(id: string) {
    setOutgoings((prev) => prev.filter((o) => o.id !== id))
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface/60 shadow-2xl shadow-black/30">
      <div className="border-b border-border bg-bg/30 px-6 py-4">
        <p className="font-display text-[10px] font-bold tracking-[0.3em] text-primary uppercase">
          ROI calculator
        </p>
        <h3 className="font-display mt-1 text-xl font-bold text-fg md:text-2xl">
          See what your squadron will actually net.
        </h3>
      </div>

      <div className="grid gap-6 p-6 md:grid-cols-[1fr_auto_1fr] md:gap-8">
        {/* Inputs */}
        <div className="space-y-8">
          {/* Tier first — everything below is bounded by this choice. */}
          <InputGroup title="Your plan">
            <div
              role="radiogroup"
              aria-label="Subscription tier"
              className="grid grid-cols-4 gap-2"
            >
              {TIER_ORDER.map((t) => {
                const active = t === tier
                return (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setTier(t)}
                    className={
                      'font-display rounded-md border px-2 py-2.5 text-[11px] font-bold tracking-wide uppercase transition-colors ' +
                      (active
                        ? 'border-primary bg-primary/15 text-fg'
                        : 'border-border text-fg-muted hover:border-fg-subtle hover:text-fg')
                    }
                  >
                    {TIER_BRAND_LABEL[t]}
                    <span className="mt-0.5 block text-[10px] font-medium tracking-normal normal-case text-fg-subtle">
                      {TIER_MONTHLY_PRICE[t] === 0
                        ? 'Free'
                        : `£${TIER_MONTHLY_PRICE[t]}/mo`}
                    </span>
                  </button>
                )
              })}
            </div>
          </InputGroup>

          {/* Members */}
          <InputGroup title="Squadron">
            <SliderField
              label="Squadron size"
              icon={<Users className="size-4" />}
              value={members}
              min={5}
              max={caps.members}
              step={5}
              display={`${members} members`}
              onChange={setMembers}
              atCeiling={members >= caps.members}
              upgrade={
                nextTier(tier)
                  ? {
                      tier: nextTier(tier)!,
                      reason: `${tierName(tier)} caps out at ${caps.members} members`,
                      onUpgrade: () => setTier(nextTier(tier)!),
                    }
                  : undefined
              }
            />
            <SliderField
              label="Monthly fee per member"
              icon={<Coins className="size-4" />}
              value={feeIdx}
              min={0}
              max={MEMBER_FEE_OPTIONS.length - 1}
              step={1}
              display={
                feePerMember === 0
                  ? 'Free squadron'
                  : `£${feePerMember.toFixed(2)} / mo`
              }
              onChange={setFeeIdx}
              help="Free, or a set fee from £2.99 to £9.99."
            />
          </InputGroup>

          {/* Tournaments */}
          <InputGroup title="Tournaments">
            <SliderField
              label="Tournaments per month"
              icon={<Trophy className="size-4" />}
              value={tournamentsPerMonth}
              min={0}
              max={12}
              step={1}
              display={
                tournamentsPerMonth === 0
                  ? 'None'
                  : `${tournamentsPerMonth} / month`
              }
              onChange={setTournamentsPerMonth}
              lockedBy={
                caps.tournaments
                  ? undefined
                  : {
                      tier: FIRST_TOURNAMENT_TIER,
                      onUpgrade: () => setTier(FIRST_TOURNAMENT_TIER),
                    }
              }
            />
            <SliderField
              label="Avg entry fee per player"
              value={avgEntryFee}
              min={0}
              max={MAX_ENTRY_FEE}
              step={ENTRY_FEE_STEP}
              display={
                avgEntryFee === 0 ? 'Free entry' : `£${avgEntryFee.toFixed(2)}`
              }
              onChange={setAvgEntryFee}
              lockedBy={
                caps.paidTournaments
                  ? undefined
                  : {
                      tier: FIRST_PAID_TIER,
                      onUpgrade: () => setTier(FIRST_PAID_TIER),
                    }
              }
            />
            <SliderField
              label="Avg prize pool per tournament"
              value={avgPrizePool}
              min={0}
              max={MAX_PRIZE_POOL}
              step={10}
              display={avgPrizePool === 0 ? '£0' : `£${avgPrizePool}`}
              onChange={setAvgPrizePool}
              help={
                result.runsPaidTournaments
                  ? `≈ ${result.playersPerTournament} players per tournament at this fee · entries balance the pot`
                  : 'Pool is what gets paid out to winners.'
              }
              lockedBy={
                caps.paidTournaments
                  ? undefined
                  : {
                      tier: FIRST_PAID_TIER,
                      onUpgrade: () => setTier(FIRST_PAID_TIER),
                    }
              }
            />
          </InputGroup>

          {/* Outgoings */}
          <InputGroup
            title="Other monthly outgoings"
            action={
              <div className="flex items-center gap-2">
                <span className="font-display text-[10px] tabular-nums text-fg-subtle">
                  {outgoings.length} / {MAX_OUTGOINGS}
                </span>
                <button
                  type="button"
                  onClick={addOutgoing}
                  disabled={atOutgoingLimit}
                  title={atOutgoingLimit ? `Max ${MAX_OUTGOINGS} rows` : undefined}
                  className="font-display inline-flex items-center gap-1 rounded-md border border-border bg-bg/40 px-2 py-1 text-[10px] font-bold tracking-[0.15em] text-fg-muted uppercase transition-colors enabled:hover:border-primary/40 enabled:hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Plus className="size-3" aria-hidden />
                  Add row
                </button>
              </div>
            }
          >
            {outgoings.length === 0 ? (
              <p className="text-xs text-fg-subtle">
                Anything else you pay for monthly — Discord Nitro, Twitch sub
                bundles, OBS plugins. Tap{' '}
                <span className="font-display font-bold text-fg">Add row</span>
                .
              </p>
            ) : (
              <ul className="space-y-2">
                {outgoings.map((o) => (
                  <OutgoingRowField
                    key={o.id}
                    row={o}
                    onChange={(patch) => updateOutgoing(o.id, patch)}
                    onRemove={() => removeOutgoing(o.id)}
                  />
                ))}
              </ul>
            )}
          </InputGroup>
        </div>

        {/* Divider */}
        <div
          aria-hidden
          className="hidden w-px self-stretch bg-border md:block"
        />

        {/* Output */}
        <div className="space-y-3">
          <SectionHeader>Income</SectionHeader>
          <ResultRow
            label="Member fees collected"
            value={formatGBP(result.memberGross)}
          />

          <SectionHeader>Outgoings</SectionHeader>
          <ResultRow
            label="Stripe · member subs"
            value={`− ${formatGBP(result.memberStripeFee)}`}
            muted
          />
          <ResultRow
            label={
              <>
                e404 ·{' '}
                <span className="font-display font-extrabold text-fg">
                  {TIER_BRAND_LABEL[tier]}
                </span>{' '}
                tier
              </>
            }
            value={
              result.tierCost === 0 ? 'Free' : `− ${formatGBP(result.tierCost)}`
            }
            muted
          />
          {result.playersOverCap && (
            <p className="mt-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs leading-relaxed text-fg-muted">
              That pool and entry fee imply{' '}
              <strong className="text-fg">{result.playersPerTournament} players</strong>,
              but {tierName(tier)} tournaments are capped at{' '}
              <strong className="text-fg">{result.tierParticipantCap}</strong>. Raise the
              entry fee, lower the pot, or size up a tier.
            </p>
          )}
          {result.e404ServiceFee > 0 && (
            <ResultRow
              label={
                <>
                  e404 service fee{' '}
                  <span className="text-fg-subtle">
                    {result.runsPaidTournaments
                      ? '· 0.4% subs + 1% pools'
                      : '· 0.4% on member subs'}
                  </span>
                </>
              }
              value={`− ${formatGBP(result.e404ServiceFee)}`}
              muted
            />
          )}
          {result.runsPaidTournaments && (
            <ResultRow
              label="Stripe · tournament entries"
              value={`− ${formatGBP(result.tournamentStripeFee)}`}
              muted
            />
          )}
          {outgoings.map((o) =>
            o.cost > 0 || o.name ? (
              <ResultRow
                key={o.id}
                label={o.name?.trim() || 'Untitled outgoing'}
                value={`− ${formatGBP(o.cost)}`}
                muted
              />
            ) : null,
          )}

          <div className="border-t border-border pt-3">
            <ResultRow
              label="Net to squadron"
              value={
                result.net >= 0
                  ? formatGBP(result.net)
                  : `−${formatGBP(Math.abs(result.net))}`
              }
              emphasised={result.net >= 0}
              negative={result.net < 0}
            />
          </div>

          {result.roiMultiple !== null && result.roiMultiple > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-success/40 bg-success/[0.06] p-3">
              <TrendingUp className="mt-0.5 size-4 shrink-0 text-success" />
              <p className="text-[11px] leading-snug text-fg-muted">
                That's{' '}
                <span className="font-display font-extrabold text-success">
                  {result.roiMultiple}× the tier cost
                </span>{' '}
                covered every month — the rest funds events, prize pools, or
                stays as squadron treasury.
              </p>
            </div>
          )}

          {result.net < 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/[0.06] p-3">
              <p className="text-[11px] leading-snug text-fg-muted">
                Outgoings outpace member fees this month. Raise the per-member
                fee, grow the roster, or trim the outgoings list to flip net
                positive.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type OutgoingRow = { id: string; name: string; cost: number }

let idCounter = 0
function nextId() {
  idCounter += 1
  return idCounter
}

function InputGroup({
  title,
  action,
  children,
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="font-display text-[10px] font-extrabold tracking-[0.3em] text-primary uppercase">
          {title}
        </p>
        {action}
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-display pt-2 text-[10px] font-extrabold tracking-[0.3em] text-fg-subtle uppercase first:pt-0">
      {children}
    </p>
  )
}

function SliderField({
  label,
  icon,
  value,
  min,
  max,
  step,
  display,
  onChange,
  help,
  lockedBy,
  atCeiling,
  upgrade,
}: {
  label: string
  icon?: React.ReactNode
  value: number
  min: number
  max: number
  step: number
  display: string
  onChange: (v: number) => void
  help?: string
  /** The control is unavailable on this plan; names the tier that unlocks it. */
  lockedBy?: { tier: SubscriptionTier; onUpgrade: () => void }
  /** The slider is sitting on its tier-imposed maximum. */
  atCeiling?: boolean
  upgrade?: { tier: SubscriptionTier; reason: string; onUpgrade: () => void }
}) {
  const locked = Boolean(lockedBy)
  return (
    <div className={locked ? 'opacity-60' : undefined}>
      <div className="flex items-center justify-between gap-3">
        <label className="font-display flex items-center gap-1.5 text-[11px] font-bold tracking-[0.18em] text-fg-subtle uppercase">
          {icon && <span className={locked ? 'text-fg-subtle' : 'text-primary'}>{icon}</span>}
          {label}
        </label>
        <span className="font-display text-sm font-extrabold tabular-nums text-fg">
          {locked ? '—' : display}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={locked ? min : value}
        disabled={locked}
        aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-3 w-full accent-primary disabled:cursor-not-allowed"
      />

      {/* A disabled control has to say WHY, and offer the way out. Greying
          something out without naming the plan that unlocks it just reads as
          broken. */}
      {lockedBy && (
        <p className="mt-2 text-[10px] leading-relaxed text-fg-subtle">
          Not available on this plan.{' '}
          <button
            type="button"
            onClick={lockedBy.onUpgrade}
            className="font-semibold text-primary underline underline-offset-2 hover:text-primary-soft"
          >
            Switch to {tierName(lockedBy.tier)}
          </button>{' '}
          to unlock it.
        </p>
      )}

      {!locked && atCeiling && upgrade && (
        <p className="mt-2 text-[10px] leading-relaxed text-fg-subtle">
          {upgrade.reason}.{' '}
          <button
            type="button"
            onClick={upgrade.onUpgrade}
            className="font-semibold text-primary underline underline-offset-2 hover:text-primary-soft"
          >
            Upgrade to {tierName(upgrade.tier)}
          </button>{' '}
          for more.
        </p>
      )}

      {!locked && help && <p className="mt-2 text-[10px] text-fg-subtle">{help}</p>}
    </div>
  )
}

function OutgoingRowField({
  row,
  onChange,
  onRemove,
}: {
  row: OutgoingRow
  onChange: (patch: Partial<OutgoingRow>) => void
  onRemove: () => void
}) {
  return (
    <li className="flex items-center gap-2">
      <input
        type="text"
        value={row.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder="Discord Nitro, Twitch sub…"
        className="font-display flex-1 rounded-md border border-border bg-bg/40 px-3 py-2 text-xs text-fg placeholder:text-fg-subtle/70 focus:border-primary/60 focus:outline-none"
      />
      <div className="relative w-24 shrink-0">
        <span className="font-display pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-fg-subtle">
          £
        </span>
        <input
          type="number"
          min={0}
          step={1}
          value={Number.isFinite(row.cost) ? row.cost : 0}
          onChange={(e) => onChange({ cost: Number(e.target.value) })}
          className="font-display w-full rounded-md border border-border bg-bg/40 py-2 pl-5 pr-2 text-right text-xs font-bold tabular-nums text-fg focus:border-primary/60 focus:outline-none"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${row.name || 'outgoing'}`}
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-danger/10 hover:text-danger"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </li>
  )
}

function ResultRow({
  label,
  value,
  muted,
  emphasised,
  negative,
}: {
  label: React.ReactNode
  value: string
  muted?: boolean
  emphasised?: boolean
  negative?: boolean
}) {
  const valueColor = negative
    ? 'text-danger'
    : emphasised
      ? 'text-success'
      : muted
        ? 'text-fg-muted'
        : 'text-fg'
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-fg-muted">{label}</span>
      <span
        className={`font-display font-bold tabular-nums ${valueColor} ${
          emphasised ? 'text-2xl' : 'text-base'
        }`}
      >
        {value}
      </span>
    </div>
  )
}

/**
 * Pick the cheapest tier that fits the squadron config.
 * Paid tournaments require BRONZE or higher even on small rosters.
 */
function formatGBP(value: number): string {
  if (value === 0) return '£0'
  return `£${value.toFixed(value % 1 === 0 ? 0 : 2)}`
}
